import React from 'react';
import styled, { css } from 'styled-components';
import MetaTags from 'components/meta-tags';
import { CommonFooterLayout } from 'src/payments/layout/common/footer';
import { CommonLayoutHeader } from 'src/payments/layout/common/header';
import { ChildrenContainer } from 'src/payments/layout/common/children-container';
import { ImageOrientation, useGetImageOrientation } from 'hooks/use-get-image-orientation';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useObserver } from 'mobx-react-lite';
import { EnrollmentSteps } from 'src/payments/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Benefits } from './benefits';
import { Faq } from './faq';
import { HowItWorks } from './how-it-works';

const {
  alreadySignedUp,
  success,
  connectBank,
  emailForm,
  passwordForm,
  forgotPasswordForm,
  signUpForm,
} = EnrollmentSteps;

// Education sections only belong under the pre-enrollment auth forms, not the bank-link,
// terms, churn, or success states.
const SIGNUP_REDESIGN_STEPS = [emailForm, passwordForm, forgotPasswordForm, signUpForm];

type DutchiePayLayoutProps = {
  children: React.ReactNode;
};

export function SignupLayout(props: DutchiePayLayoutProps): JSX.Element | null {
  const { children } = props;
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const flags = useFlags();
  const signupRedesignEnabled = flags[`fintech.platform.pbb-signup-redesign.rollout`] ?? false;
  const dispensaryLogo = useObserver(() => DutchiePayEnrollment.dispensary.logo ?? '');
  const dispensaryName = useObserver(() => DutchiePayEnrollment.dispensary.name ?? `Company`);
  const currentStep = useObserver(() => DutchiePayEnrollment.currentStep);
  const isCobrandedSignup = useObserver(() => DutchiePayEnrollment.isCobrandedSignup);
  const plaidModal = currentStep === connectBank;
  const currentStepEqualsSuccess = currentStep === success || currentStep === alreadySignedUp;
  const showSignupRedesignSections = signupRedesignEnabled && SIGNUP_REDESIGN_STEPS.includes(currentStep);
  const { imageOrientation } = useGetImageOrientation(dispensaryLogo);

  return (
    <>
      <MetaTags />
      <Container cardView={!currentStepEqualsSuccess && !plaidModal}>
        <CommonLayoutHeader />
        <ContentContainer $redesign={showSignupRedesignSections}>
          {dispensaryLogo && isCobrandedSignup && (
            <LogoContainer $redesign={showSignupRedesignSections}>
              <DispensaryLogo
                src={dispensaryLogo}
                orientation={imageOrientation}
                alt={`${String(dispensaryName)} Logo`}
              />
            </LogoContainer>
          )}
          <ChildrenContainer cardView={!currentStepEqualsSuccess && !plaidModal}>{children}</ChildrenContainer>
          {showSignupRedesignSections && (
            <>
              <HowItWorks />
              <Benefits />
              <Faq />
            </>
          )}
          {!currentStepEqualsSuccess && <SignupFooter $redesign={showSignupRedesignSections} />}
        </ContentContainer>
      </Container>
    </>
  );
}

const Container = styled.div`
  /* Setting css vars on a parent scope makes them available to all child dom nodes */
  --dp-content-width: 375px;
  height: ${(props) => (props.cardView ? `100%` : '100vh')};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 24px;
`;

const ContentContainer = styled.div<{ $redesign: boolean }>`
  max-width: var(--dp-content-width);
  padding: 8px;
  width: 100%;

  ${({ $redesign }) =>
    $redesign &&
    css`
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 24px 8px;
    `}
`;

const SignupFooter = styled(CommonFooterLayout)<{ $redesign: boolean }>`
  ${({ $redesign }) =>
    $redesign &&
    css`
      margin-top: 0;
    `}
`;

const LogoContainer = styled.div<{ $redesign: boolean }>`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ $redesign }) => ($redesign ? '0' : '16px')};
`;

const DispensaryLogo = styled.img`
  max-height: ${(props) => (props.orientation === ImageOrientation.vertical ? '32px' : '56px')};
`;
